import { type Theme } from '@mui/material/styles';

import { type QuantitativeSize } from '../../types/sizes';
import { parseQuantitativeSize } from '../../util/sizes';

import * as types from './types';

type AvatarBaseProperties = {
  readonly fontSize: `${string}rem`;
  readonly gravatarSize: number;
  readonly size: number;
};

export const DefaultAvatarDiscreteSize = types.AvatarDiscreteSize.Large;

export const AvatarSizeProperties = {
  [types.AvatarDiscreteSize.Large]: {
    fontSize: '1.25rem',
    gravatarSize: 100,
    size: 40,
  },
  [types.AvatarDiscreteSize.Small]: {
    fontSize: '0.8125rem',
    gravatarSize: 100,
    size: 24,
  },
  [types.AvatarDiscreteSize.XLarge]: {
    fontSize: '6rem',
    gravatarSize: 400,
    size: 196,
  },
} as const satisfies Record<types.AvatarDiscreteSize, AvatarBaseProperties>;

export type AvatarSizePropertySet<S extends types.AvatarDiscreteSize = types.AvatarDiscreteSize> =
  (typeof AvatarSizeProperties)[S];

export const getAvatarSize = (size: QuantitativeSize<'px'> | types.AvatarDiscreteSize): number => {
  if (types.isAvatarDiscreteSize(size)) {
    return AvatarSizeProperties[size].size;
  }
  const parsed = parseQuantitativeSize(size);
  return parsed[0];
};

export const getAvatarFontSize = (size: types.AvatarSize): QuantitativeSize<'rem'> => {
  if (types.isAvatarDiscreteSize(size)) {
    return AvatarSizeProperties[size].fontSize;
  }
  return AvatarSizeProperties[types.AvatarDiscreteSize.Large].fontSize;
};

export const DefaultAvatarStyleProps = {
  backgroundColor: (t: Theme) => t.palette.other.avatarFill,
  borderColor: (t: Theme) => t.palette.state.secondary.borderColor,
  color: (t: Theme) => t.palette.primary.main,
  fontSize: getAvatarFontSize(DefaultAvatarDiscreteSize),
  size: DefaultAvatarDiscreteSize,
} as const satisfies Required<types.AvatarStyleProps>;
